<template>
    <div class="permissions-page">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h2 class="mb-0">Role permissions</h2>
            <div class="d-flex">
                <button class="btn btn-primary d-flex align-items-center mr-2" type="button" :disabled="loading" @click="openUsersModal">
                    <span class="material-icons-outlined mr-1">group</span>
                    User levels
                </button>
                <button class="btn btn-success d-flex align-items-center" type="button" :disabled="loading || saving" @click="save">
                    <span class="material-icons-outlined mr-1">save</span>
                    {{ saving ? 'Saving...' : 'Save' }}
                </button>
            </div>
        </div>

        <div v-if="alert.message" class="alert" :class="'alert-' + alert.type" role="alert">
            {{ alert.message }}
        </div>

        <div v-if="loading" class="text-muted">Loading permissions...</div>

        <template v-if="showUsersModal">
            <div class="modal fade show d-block" tabindex="-1" role="dialog" @click.self="showUsersModal = false">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">User levels</h5>
                            <button type="button" class="close" aria-label="Close" @click="showUsersModal = false">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="modal-body">
                            <div v-if="modalAlert.message" class="alert" :class="'alert-' + modalAlert.type" role="alert">
                                {{ modalAlert.message }}
                            </div>
                            <p class="text-muted mb-2">Assign internal users to a level. Changes are saved immediately.</p>
                            <div class="table-responsive permissions-users-table">
                                <table class="table table-bordered bg-white mb-0">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th class="permissions-level-column">Level</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="user in users" :key="user.id">
                                            <td class="align-middle">{{ user.name }}</td>
                                            <td class="align-middle">{{ user.email }}</td>
                                            <td>
                                                <select
                                                    class="form-control"
                                                    :disabled="assigningUserId === user.id || user.is_current"
                                                    :title="user.is_current ? 'You cannot change your own level.' : null"
                                                    :value="user.role || ''"
                                                    @change="assignRole(user, $event.target.value)"
                                                >
                                                    <option v-if="!user.role" value="" disabled>No level</option>
                                                    <option v-for="role in roles" :key="role.slug" :value="role.slug">
                                                        {{ role.name }}
                                                    </option>
                                                </select>
                                            </td>
                                        </tr>
                                        <tr v-if="!users.length">
                                            <td colspan="3" class="text-muted">No users</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" @click="showUsersModal = false">Close</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-backdrop fade show"></div>
        </template>

        <div v-if="!loading" class="table-responsive">
            <table class="table table-bordered bg-white permissions-table">
                <thead>
                    <tr>
                        <th class="align-bottom permissions-label-column">Permission</th>
                        <th v-for="role in roles" :key="role.slug" class="text-center permissions-role-column">
                            <div class="font-weight-bold">{{ role.name }}</div>
                            <small class="text-muted">{{ role.users.length }} user(s)</small>
                            <div class="permissions-users-list text-left mt-2">
                                <template v-if="role.users.length">
                                    <div v-for="user in role.users" :key="user.id" class="text-truncate">
                                        {{ user.email }}
                                    </div>
                                </template>
                                <span v-else class="text-muted">No users</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="group in permissionGroups">
                        <tr :key="group.group" class="bg-page">
                            <th :colspan="roles.length + 1">{{ group.group }}</th>
                        </tr>
                        <tr v-for="permission in group.permissions" :key="permission.key">
                            <td>
                                <strong>{{ permission.name }}</strong>
                                <br>
                                <small class="text-muted">{{ permission.key }}</small>
                            </td>
                            <td v-for="role in roles" :key="role.slug + '-' + permission.key" class="text-center align-middle">
                                <input
                                    type="checkbox"
                                    :checked="hasPermission(role, permission.key)"
                                    @change="togglePermission(role, permission.key, $event.target.checked)"
                                >
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Permissions',
    props: {
        dataUrl: {type: String, required: true},
        updateUrl: {type: String, required: true},
        assignUrl: {type: String, required: true},
    },
    data() {
        return {
            roles: [],
            permissionGroups: [],
            users: [],
            assigningUserId: null,
            showUsersModal: false,
            loading: true,
            saving: false,
            alert: {
                type: 'success',
                message: '',
            },
            modalAlert: {
                type: 'success',
                message: '',
            },
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            return axios.get(this.dataUrl)
                .then((response) => {
                    this.roles = response.data.roles;
                    this.permissionGroups = response.data.permissionGroups;
                    this.users = response.data.users;
                    this.alert.message = '';
                })
                .catch(() => {
                    this.showAlert('danger', 'Failed to load permissions.');
                })
                .then(() => {
                    this.loading = false;
                });
        },
        openUsersModal() {
            this.modalAlert.message = '';
            this.showUsersModal = true;
        },
        assignRole(user, role) {
            this.assigningUserId = user.id;
            axios.patch(this.assignUrl + '/' + user.id, {role})
                .then(() => ({type: 'success', message: user.email + ' moved to ' + this.roleName(role) + '.'}))
                .catch((error) => ({
                    type: 'danger',
                    message: error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'Failed to update user level.',
                }))
                .then((alert) => {
                    this.assigningUserId = null;
                    return this.load().then(() => {
                        this.modalAlert = alert;
                    });
                });
        },
        roleName(slug) {
            const role = this.roles.find((item) => item.slug === slug);
            return role ? role.name : slug;
        },
        hasPermission(role, permissionKey) {
            return role.permissions.indexOf(permissionKey) !== -1;
        },
        togglePermission(role, permissionKey, checked) {
            const permissions = role.permissions.slice();
            const index = permissions.indexOf(permissionKey);

            if (checked && index === -1) {
                permissions.push(permissionKey);
            }

            if (!checked && index !== -1) {
                permissions.splice(index, 1);
            }

            this.$set(role, 'permissions', permissions);
        },
        save() {
            this.saving = true;
            const requests = this.roles.map((role) => {
                return axios.patch(this.updateUrl + '/' + role.id, {
                    permissions: role.permissions,
                });
            });

            Promise.all(requests)
                .then(() => {
                    window.location.reload();
                })
                .catch(() => {
                    this.showAlert('danger', 'Failed to save permissions.');
                    this.saving = false;
                });
        },
        showAlert(type, message) {
            this.alert = {type, message};
        },
    },
};
</script>

<style scoped>
.permissions-label-column {
    width: 260px;
}

.permissions-role-column {
    min-width: 220px;
    vertical-align: bottom;
}

.permissions-users-list {
    font-size: 12px;
    max-height: 110px;
    overflow-y: auto;
}

.permissions-users-table {
    max-height: 420px;
    overflow-y: auto;
}

.permissions-level-column {
    width: 220px;
}

.permissions-table input[type="checkbox"] {
    width: 16px;
    height: 16px;
}
</style>
